import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";

import {
  BookingFollowUpStatus,
  BookingFollowUpType,
  Employee,
} from "../../process/types/processTypes";
import { FollowUpStatusChip } from "./FollowUpStatusChip";
import {
  effectiveFollowUpStatus,
  embassyTargetStatuses,
  getFollowUpForType,
  nextFollowUpStatus,
  statusOrderForType,
} from "../utils/followUpHelpers";

interface Props {
  employee: Employee;
  type: BookingFollowUpType;
  targetStatus?: BookingFollowUpStatus;
  onSubmit: (payload: {
    status: BookingFollowUpStatus;
    scheduledDate?: Date;
    remark?: string;
  }) => Promise<void>;
  onClose: () => void;
  submitting?: boolean;
  locale?: string;
}

export function FollowUpAdvanceDialog({
  employee,
  type,
  targetStatus,
  onSubmit,
  onClose,
  submitting,
  locale = "en",
}: Props) {
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");

  const followUp = getFollowUpForType(employee, type);
  const currentStatus = effectiveFollowUpStatus(followUp);
  const isEmbassy = type === BookingFollowUpType.embassy;
  const isLmis = type === BookingFollowUpType.lmis;
  const embassyTargets = useMemo(
    () => (isEmbassy ? embassyTargetStatuses(currentStatus) : []),
    [isEmbassy, currentStatus],
  );

  const [embassyStatus, setEmbassyStatus] = useState<BookingFollowUpStatus>(
    () => embassyTargets[0] ?? currentStatus,
  );
  const [scheduledDate, setScheduledDate] = useState<Date | null>(() => {
    if (followUp?.scheduledDate) return new Date(followUp.scheduledDate);
    // Default embassy submission / LMIS issued date to today.
    return isEmbassy || isLmis ? new Date() : null;
  });
  const [remark, setRemark] = useState(followUp?.remark ?? "");

  const status = isEmbassy
    ? embassyStatus
    : targetStatus ?? nextFollowUpStatus(currentStatus, type) ?? currentStatus;

  // Embassy "submitted to embassy" captures the submission date; LMIS Issued
  // captures the issued date; the other date-bearing status is "booked with date".
  const isEmbassySubmission =
    isEmbassy && status === BookingFollowUpStatus.embassy_in_progress;
  const isLmisIssuedStatus = status === BookingFollowUpStatus.lmis_issued;
  const needsDate =
    status === BookingFollowUpStatus.booked_with_date ||
    isEmbassySubmission ||
    isLmisIssuedStatus;

  const syncPreview = useMemo(() => {
    if (isEmbassy) {
      return status === BookingFollowUpStatus.embassy_stamped
        ? t("followUps.syncEmbassyStamped")
        : null;
    }
    if (isLmisIssuedStatus) {
      return t("followUps.syncLmisIssued");
    }
    if (status !== BookingFollowUpStatus.employee_completed) return null;
    switch (type) {
      case BookingFollowUpType.medical:
        return te("completed");
      case BookingFollowUpType.tasheer:
        return t("followUps.syncBiometrics");
      case BookingFollowUpType.flight:
        return t("followUps.syncFlight");
      default:
        return null;
    }
  }, [status, type, isEmbassy, isLmisIssuedStatus, t, te]);

  const handleSave = async () => {
    if (needsDate && !scheduledDate) {
      return;
    }
    await onSubmit({
      status,
      scheduledDate: needsDate ? (scheduledDate ?? undefined) : undefined,
      remark: remark.trim() || undefined,
    });
  };

  const statusOrder = statusOrderForType(type);
  const activeStep = statusOrder.indexOf(status);

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {!isEmbassy && (
        <Stepper activeStep={activeStep >= 0 ? activeStep : 0} alternativeLabel>
          {statusOrder.map((step) => (
            <Step key={step} completed={statusOrder.indexOf(step) < activeStep}>
              <StepLabel>{te(`bookingFollowUp_${step}`)}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      <Box>
        <FollowUpStatusChip status={currentStatus} />
      </Box>

      {isEmbassy && embassyTargets.length > 0 && (
        <FormControl fullWidth size="small">
          <InputLabel>{t("followUps.status")}</InputLabel>
          <Select
            label={t("followUps.status")}
            value={embassyStatus}
            onChange={(e) =>
              setEmbassyStatus(e.target.value as BookingFollowUpStatus)
            }
          >
            {embassyTargets.map((option) => (
              <MenuItem key={option} value={option}>
                {te(`bookingFollowUp_${option}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {needsDate && (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
          <DatePicker
            label={
              isEmbassySubmission
                ? t("followUps.submittedDate")
                : isLmisIssuedStatus
                  ? t("lmisIssuedDate")
                  : t("followUps.scheduledDate")
            }
            views={["year", "month", "day"]}
            disableMaskedInput
            inputFormat="DD-MMM-YYYY"
            value={scheduledDate}
            onChange={(date) => setScheduledDate(date as Date | null)}
            renderInput={(params) => (
              <TextField {...params} fullWidth size="small" required />
            )}
          />
        </LocalizationProvider>
      )}

      {status === BookingFollowUpStatus.confirmed_by_employee &&
        followUp?.scheduledDate && (
          <Alert severity="info">
            {t("followUps.confirmPrompt", {
              date: new Date(followUp.scheduledDate).toLocaleDateString(),
            })}
          </Alert>
        )}

      {syncPreview && (
        <Alert severity="success">{t("followUps.willSync", { field: syncPreview })}</Alert>
      )}

      <TextField
        label={t("remark")}
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        size="small"
      />

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onClose} disabled={submitting}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={submitting || (needsDate && !scheduledDate)}
        >
          {te(`bookingFollowUp_${status}`)}
        </Button>
      </Stack>
    </Stack>
  );
}
